import { Box, Flex, Heading, Tag, type TagProps } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type EspdPreviewOwner = "autoFilled" | "buyer" | "supplier" | "conditional" | "fixed";

const OWNER_COLOR_SCHEME: Record<EspdPreviewOwner, TagProps["colorScheme"]> = {
  autoFilled: "teal",
  buyer: "teal",
  supplier: "blue",
  conditional: "gray",
  fixed: "gray",
};

const useOwnerLabel = (owner: EspdPreviewOwner): string => {
  const { t } = useTranslation();
  switch (owner) {
    case "autoFilled": return t("Auto-filled");
    case "buyer": return t("Configured by contracting authority");
    case "supplier": return t("Filled in by supplier");
    case "conditional": return t("Conditional");
    case "fixed": return t("Standard text");
  }
};

interface Props {
  eyebrow: string;
  title: string;
  owner: EspdPreviewOwner;
  children: ReactNode;
}

export const EspdPreviewPart = ({ eyebrow, title, owner, children }: Props) => {
  const ownerLabel = useOwnerLabel(owner);
  return (
    <Box py="5" borderBottom="1px solid" borderColor="smBorder" _last={{ borderBottom: 0 }}>
      <Flex align="baseline" gap="3" mb="3">
        <Box fontSize="xs" textTransform="uppercase" letterSpacing="wider" color="smMuted" fontWeight="semibold">
          {eyebrow}
        </Box>
        <Heading as="h3" size="sm" m="0">
          {title}
        </Heading>
        <Tag ml="auto" size="sm" variant="subtle" colorScheme={OWNER_COLOR_SCHEME[owner]} fontWeight="medium">
          {ownerLabel}
        </Tag>
      </Flex>
      <Box bg="smBackgroundSecondary" borderRadius="md" p="4">
        {children}
      </Box>
    </Box>
  );
};
